// Frontend logging utility with server forwarding for errors and warnings

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

// Buffer for batched log forwarding
let logBuffer = []
let flushTimeout = null
const FLUSH_INTERVAL_MS = 5000 // Flush every 5 seconds

class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development'
    this.isProduction = process.env.NODE_ENV === 'production'
  }

  // Generate a simple session ID for correlation
  getSessionId() {
    if (typeof window !== 'undefined') {
      let sessionId = sessionStorage.getItem('poll_session_id')
      if (!sessionId) {
        sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2)
        sessionStorage.setItem('poll_session_id', sessionId)
      }
      return sessionId
    }
    return 'server'
  }

  /**
   * Queue a log entry for forwarding to the server.
   * Only errors and warnings are forwarded.
   */
  queueForForwarding(level, message, context) {
    if (level !== 'warn' && level !== 'error') {
      return
    }

    logBuffer.push({
      level,
      message,
      context: this.sanitizeContext(context),
      sessionId: this.getSessionId(),
      timestamp: new Date().toISOString()
    })

    // Schedule a flush if not already scheduled
    if (!flushTimeout) {
      flushTimeout = setTimeout(() => this.flushLogs(), FLUSH_INTERVAL_MS)
    }
  }

  /**
   * Send buffered logs to the server (fire-and-forget).
   */
  async flushLogs() {
    if (flushTimeout) {
      clearTimeout(flushTimeout)
      flushTimeout = null
    }

    if (logBuffer.length === 0) {
      return
    }

    const logsToSend = [...logBuffer]
    logBuffer = []

    try {
      await fetch(`${API_BASE_URL}/api/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs: logsToSend })
      })
    } catch (e) {
      // Silently fail - don't create infinite loop
      // Could optionally log to console in development
      if (this.isDevelopment) {
        console.debug('[Logger] Failed to forward logs to server', e.message)
      }
    }
  }

  log(level, message, context = {}) {
    const timestamp = new Date().toISOString()
    const logEntry = {
      timestamp,
      level,
      message,
      sessionId: this.getSessionId(),
      ...context
    }

    // In development, log everything to console
    if (this.isDevelopment) {
      console[level](`[${level.toUpperCase()}]`, message, logEntry)
    }

    // In production, only log warnings and errors to console
    if (this.isProduction && (level === 'warn' || level === 'error')) {
      console[level](`[${level.toUpperCase()}]`, message, {
        ...logEntry,
        // Remove potentially sensitive context in production console
        context: this.sanitizeContext(context)
      })
    }

    // Forward errors and warnings to server (both dev and prod)
    this.queueForForwarding(level, message, context)
  }

  // Remove potentially sensitive data from production logs
  sanitizeContext(context) {
    const sanitized = { ...context }
    // Remove any fields that might contain user input or sensitive data
    const sensitiveFields = ['password', 'token', 'key', 'secret']
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]'
      }
    })
    return sanitized
  }

  debug(message, context) {
    this.log('debug', message, context)
  }

  info(message, context) {
    this.log('info', message, context)
  }

  warn(message, context) {
    this.log('warn', message, context)
  }

  error(message, context) {
    this.log('error', message, context)
  }
}

// Create singleton instance
const logger = new Logger()

// Flush logs before page unload to ensure no logs are lost
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    logger.flushLogs()
  })

  // Also flush when the page becomes hidden (mobile tab switch, etc.)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      logger.flushLogs()
    }
  })
}

export default logger

